import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

const fadeInAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const fadeOutAnimation = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.3);
  }
`;

const pulseAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1);
  }
`;

const LightsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
`;

const LightDot = styled.div<{ 
  x: number; 
  y: number; 
  delay: number; 
  duration: number;
  size: number;
  color: string;
  isVisible: boolean;
  isNew: boolean;
}>`
  position: absolute;
  left: ${props => props.x}%;
  top: ${props => props.y}%;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  background: radial-gradient(circle, ${props => props.color} 0%, transparent 70%);
  animation: 
    ${props => props.isNew ? fadeInAnimation : props.isVisible ? pulseAnimation : fadeOutAnimation} 
    ${props => props.isNew ? '3s' : props.isVisible ? `${props.duration}s` : '3s'} 
    ease-in-out 
    ${props => props.isVisible ? 'infinite' : 'forwards'};
  animation-delay: ${props => props.delay}s;
  z-index: 0;
  transition: all 2s ease-in-out;
  will-change: opacity, transform;
`;


interface LightPoint {
  id: number;
  x: number;
  y: number;
  delay: number;
  duration: number;
  size: number;
  color: string;
  isVisible: boolean;
  isNew: boolean;
  timestamp: number;
  lastPositionChange: number;
}

const PulsingLights: React.FC = () => {
  const [lights, setLights] = useState<LightPoint[]>([]);
  const nextIdRef = useRef(0);

  useEffect(() => {
    const colors = [
      'rgba(81, 194, 201, 1.0)', // Same base color for all at max brightness
    ];

    const createLight = (): LightPoint => ({
      id: nextIdRef.current++,
      x: 10 + Math.random() * 80, // Keep lights away from edges
      y: 20 + Math.random() * 60, // Distribute across full height
      delay: Math.random() * 6, // Staggered start times (0-6 seconds)
      duration: 4, // Same rate for all lights (4 second cycle)
      size: 10, // Same size for all lights
      color: colors[0], // Same color for all lights
      isVisible: true,
      isNew: true,
      timestamp: Date.now(),
      lastPositionChange: Date.now()
    });

    // Initialize with some lights
    const initialLights = Array.from({ length: 14 }, createLight);
    setLights(initialLights);

    // Mark lights as no longer new after fade-in
    setTimeout(() => {
      setLights(prev => prev.map(light => ({ ...light, isNew: false })));
    }, 3000);

    // Position update system - moves lights during their invisible phase
    const positionUpdateInterval = setInterval(() => {
      setLights(prev => prev.map(light => {
        const now = Date.now();
        const timeSinceStart = (now - light.timestamp - light.delay * 1000) % (light.duration * 1000);
        const cycleProgress = timeSinceStart / (light.duration * 1000);
        
        // Only move position when light is in invisible phase (0-25% or 75-100% of cycle)
        // and hasn't been moved recently
        const isInvisiblePhase = cycleProgress < 0.25 || cycleProgress > 0.75;
        const timeSinceLastMove = now - light.lastPositionChange;
        const shouldMove = isInvisiblePhase && timeSinceLastMove > 8000; // Move every 8+ seconds
        
        if (shouldMove && Math.random() > 0.7) { // 30% chance to move when conditions are met
          return {
            ...light,
            x: 10 + Math.random() * 80,
            y: 20 + Math.random() * 60,
            lastPositionChange: now
          };
        }
        
        return light;
      }));
    }, 1000); // Check every second for position updates

    // Periodically add new lights and remove old ones
    const interval = setInterval(() => {
      setLights(prev => {
        const now = Date.now();
        const maxAge = 25000; // Balanced lifespan: 25 seconds
        
        // Mark old lights for removal (fade out)
        const updatedLights = prev.map(light => ({
          ...light,
          isVisible: now - light.timestamp < maxAge
        }));

        // Remove lights that have been invisible for a while (completely faded out)
        const visibleLights = updatedLights.filter(light => 
          light.isVisible || now - light.timestamp < maxAge + 4000 // 4 second fade-out buffer
        );

        // Add new lights if we have fewer than desired
        const targetCount = 11 + Math.floor(Math.random() * 6); // 11-16 lights
        const visibleCount = visibleLights.filter(light => light.isVisible).length;
        
        if (visibleCount < targetCount) {
          const newLightsCount = Math.min(3, targetCount - visibleCount);
          const newLights = Array.from({ length: newLightsCount }, createLight);
          
          // Mark new lights as no longer new after fade-in
          setTimeout(() => {
            setLights(current => current.map(light => 
              newLights.some(newLight => newLight.id === light.id) 
                ? { ...light, isNew: false }
                : light
            ));
          }, 3000);
          
          return [...visibleLights, ...newLights];
        }

        return visibleLights;
      });
    }, 8000); // Less frequent updates for smoother experience

    return () => {
      clearInterval(interval);
      clearInterval(positionUpdateInterval);
    };
  }, []);

  return (
    <LightsContainer>
      {lights.map((light) => (
        <LightDot
          key={light.id}
          x={light.x}
          y={light.y}
          delay={light.delay}
          duration={light.duration}
          size={light.size}
          color={light.color}
          isVisible={light.isVisible}
          isNew={light.isNew}
        />
      ))}
    </LightsContainer>
  );
};

export default PulsingLights;

